// Story 10.6 — the logged-out `/d/{dossier_id}` sealed-dossier permalink.
// Shows the sealed artifact + its PUBLISHED tier odds (resolved server-side
// per ToS §6), and nothing about the purchase or what is inside (a sealed
// dossier's creator + tier do not exist until the reveal roll). Renders the
// same <SealedDossier> the owner sees.

import { useParams } from "react-router-dom";
import { SealedDossier, TierBadge, type SealedDossierData } from "@greenhold/components";

import { usePublicSealedDossier } from "../../features/public-collection/usePublicCollection";
import type { PublicSealedDossierDetail } from "../../features/public-collection/publicCollectionTypes";
import { PublicShell, PublicNotFound, PublicLoading } from "./PublicShell";

const TIERS = ["common", "preferred", "premium", "founders"] as const;

function dossierData(dto: PublicSealedDossierDetail): SealedDossierData {
  return {
    id: dto.sealed_dossier_id,
    drop_id: dto.drop_id,
    edition_number: dto.edition_number,
    portfolio_title: dto.portfolio_title,
    drop_status: dto.drop_status,
    retiring_at: dto.retiring_at,
    seal_color: dto.seal_color,
  };
}

function pct(n: number): string {
  return `${(n * 100).toFixed(n < 0.01 ? 2 : 1)}%`;
}

export function PublicSealedDossierPage() {
  const { dossierId = "" } = useParams();
  const query = usePublicSealedDossier(dossierId);

  if (query.isLoading) return <PublicLoading />;
  if (query.data?.notFound || !query.data?.data) {
    return (
      <PublicNotFound
        title="This dossier isn't public"
        detail="The link may be wrong, its owner has taken it down, or it has been opened."
      />
    );
  }

  const dto = query.data.data;
  const odds = dto.tier_distribution;
  return (
    <PublicShell>
      <div className="flex flex-wrap items-start justify-center gap-10">
        <div className="w-[220px] shrink-0">
          <SealedDossier
            dossier={dossierData(dto)}
            size="md"
            state="selected"
            game={dto.portfolio_theme ?? undefined}
          />
        </div>
        <div className="min-w-[260px] max-w-md space-y-4">
          <h1 className="font-heading text-2xl font-bold">
            {dto.portfolio_title}
          </h1>
          <p className="text-sm opacity-70">
            Sealed dossier · Issue #{dto.edition_number}
          </p>
          <div className="space-y-2">
            <h2 className="font-heading text-sm font-semibold opacity-80">
              Published odds
            </h2>
            <ul className="space-y-1 text-sm">
              {TIERS.map((t) => (
                <li key={t} className="flex items-center justify-between gap-4">
                  <span className="flex items-center gap-2">
                    <TierBadge tier={t} size="sm" />
                    <span className="capitalize">{t}</span>
                  </span>
                  <span className="font-mono">{pct(odds[t])}</span>
                </li>
              ))}
            </ul>
            <p className="text-[11px] opacity-50">
              These are the exact probabilities the reveal uses (ToS §6).
            </p>
          </div>
        </div>
      </div>
    </PublicShell>
  );
}
